import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Feature } from '@freelancer/feature-flags';
import { CalloutColor, CalloutPlacement } from '@freelancer/ui/callout';
import { IconColor, IconSize, flAi, provideFlIcons } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkIconPosition,
  LinkUnderline,
  LinkWeight,
} from '@freelancer/ui/link';
import { LogoSize } from '@freelancer/ui/logo';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';

@Component({
  providers: [provideFlIcons({ flAi })],
  standalone: false,
  selector: 'app-navigation-logged-out-home-section-solutions',
  template: `
    <fl-callout
      class="Callout"
      [hover]="true"
      [edgeToEdge]="true"
      [color]="CalloutColor.DARK"
      [hideCloseButton]="true"
      [crawlable]="true"
      [placement]="CalloutPlacement.BOTTOM_RIGHT"
      [overflowContent]="true"
      (calloutOpen)="openCalloutHandler()"
      (calloutClose)="closeCalloutHandler()"
    >
      <fl-callout-trigger class="Callout">
        <fl-link
          class="NavigationItem mr-small mobile:hide tablet:hide"
          flTrackingLabel="Solutions"
          i18n="Navigation solutions label"
          [class.IsCalloutOpen]="isCalloutOpen"
          [iconName]="'ui-chevron-down'"
          [iconSize]="IconSize.MID"
          [iconColor]="IconColor.INHERIT"
          [iconPosition]="LinkIconPosition.RIGHT"
          [size]="TextSize.BODY_16"
          [color]="LinkColor.LIGHT"
          [underline]="LinkUnderline.NEVER"
        >
          Solutions
        </fl-link>
      </fl-callout-trigger>

      <fl-callout-content>
        <div class="Container">
          <div class="NavContainer">
            <fl-grid class="GridNavContainer">
              <fl-col [col]="6">
                <app-navigation-menu-nav-item
                  class="CardLink mb-mid"
                  i18n-titleLink="Enterprise navigation item title text"
                  titleLink="Enterprise"
                  i18n-description="Enterprise navigation item description text"
                  description="Power your competitive advantage with Freelancer Enterprise."
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconColor]="IconColor.INHERIT"
                  [iconName]="'ui-globe'"
                  [iconArrow]="false"
                  [isActive]="true"
                  [url]="'/enterprise'"
                ></app-navigation-menu-nav-item>
                <app-navigation-menu-nav-item
                  class="CardLink mb-mid"
                  i18n-titleLink="Field Services navigation item title text"
                  titleLink="Field Services"
                  i18n-description="
                    Field Services navigation item description text
                  "
                  description="Deliver expertise anywhere in the world at scale - on demand."
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconArrow]="false"
                  [isActive]="true"
                  [iconName]="'ui-handyman'"
                  [iconColor]="IconColor.WARNING"
                  [url]="'/globalfleet'"
                ></app-navigation-menu-nav-item>
                <app-navigation-menu-nav-item
                  class="CardLink mb-xlarge"
                  i18n-titleLink="AI business navigation item title text"
                  titleLink="AI for business"
                  i18n-description="
                    AI business navigation item description text
                  "
                  description="Let experts in the latest AI technology transform your business."
                  [iconColor]="IconColor.INHERIT"
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconArrow]="false"
                  [isActive]="true"
                  [url]="'/ai'"
                  [useIconFont]="false"
                  [iconName]="'flAi'"
                ></app-navigation-menu-nav-item>
                <div class="ColHeader mb-small">
                  <fl-logo
                    class="mr-xxxsmall"
                    [size]="LogoSize.MINI"
                  ></fl-logo>
                  <p
                    class="text-light font-bold"
                    i18n="Freelancer products header"
                  >
                    Freelancer products
                  </p>
                </div>
                <app-navigation-logged-out-home-section-products></app-navigation-logged-out-home-section-products>
              </fl-col>
              <fl-col [col]="6">
                <app-navigation-menu-nav-item
                  *flFeature="
                    Feature.INNOVATION_CHALLENGES;
                    else innovationChallengesOff
                  "
                  class="CardLink mb-mid"
                  i18n-titleLink="Innovation Challenges item title text"
                  titleLink="Innovation Challenges"
                  i18n-description="Innovation Challenges item description text"
                  description="Turn challenges into breakthroughs with the largest innovation hub."
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconName]="'ui-rocket-launch'"
                  [iconColor]="IconColor.RECRUITER_LIGHT"
                  [iconArrow]="false"
                  [isActive]="true"
                  [url]="'/innovation'"
                ></app-navigation-menu-nav-item>
                <app-navigation-menu-nav-item
                  class="CardLink mb-mid"
                  i18n-titleLink="Freelancer API navigation item title text"
                  titleLink="Freelancer API"
                  i18n-description="
                    Freelancer API navigation item description text
                  "
                  description="Use the Freelancer API to access a cloud workforce of skilled freelancers."
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconArrow]="false"
                  [isActive]="true"
                  [iconName]="'ui-api'"
                  [iconColor]="IconColor.CONTEST"
                  [url]="'https://developers.freelancer.com/'"
                ></app-navigation-menu-nav-item>
                <app-navigation-menu-nav-item
                  class="CardLink mb-xlarge"
                  i18n-titleLink="Local jobs navigation item title text"
                  titleLink="Local jobs"
                  i18n-description="Local jobs navigation item description text"
                  description="Get help in any location, anywhere in the world."
                  [descriptionColor]="FontColor.LIGHT"
                  [isLoggedOut]="true"
                  [doubleColumn]="true"
                  [iconArrow]="false"
                  [isActive]="true"
                  [url]="'/local'"
                  [iconColor]="IconColor.SUCCESS"
                  [iconName]="'ui-pin-drop'"
                ></app-navigation-menu-nav-item>
                <div class="ColHeader mb-small">
                  <fl-logo
                    class="mr-xxxsmall"
                    [size]="LogoSize.MINI"
                  ></fl-logo>
                  <p
                    class="text-light font-bold"
                    i18n="Freelancer services header"
                  >
                    Freelancer services
                  </p>
                </div>
                <app-navigation-logged-out-home-section-services>
                </app-navigation-logged-out-home-section-services>
              </fl-col>
            </fl-grid>
          </div>
          <div class="PannelContainer">
            <p
              class="text-light font-bold mb-mid"
              i18n="How it works title card"
            >
              How it works
            </p>
            <fl-grid class="GridSideContainer">
              <fl-col [col]="6">
                <app-navigation-menu-card
                  class="mb-mid"
                  i18n-description="
                    Finding the best freelancer article description
                  "
                  description="How to hire the perfect freelancer for your project"
                  [url]="
                    '/articles/starting-your-business/finding-the-best-freelancer'
                  "
                  [imgSrc]="
                    'logged-out-navigation/solutions/how-it-works-1.jpg'
                  "
                  [readMore]="true"
                  [longDescription]="true"
                  [descriptionWeight]="FontWeight.NORMAL"
                  [isLoggedOut]="true"
                >
                </app-navigation-menu-card>
                <div class="ColHeader mb-small">
                  <fl-icon
                    class="ViewMoreColor mr-xxsmall"
                    [name]="'ui-lightbulb'"
                    [useIconFont]="true"
                    [fillIconFont]="false"
                    [color]="IconColor.INHERIT"
                    [size]="IconSize.SMALL"
                  ></fl-icon>

                  <p
                    class="text-light font-bold"
                    i18n="Get ideas header"
                  >
                    Get ideas
                  </p>
                </div>
                <div class="LinksColumn">
                  <app-navigation-logged-out-home-section-get-ideas
                    class="GetIdeas"
                  ></app-navigation-logged-out-home-section-get-ideas>
                  <fl-link
                    flTrackingLabel="ViewAllClick"
                    i18n="View all link"
                    class="ViewMoreColor"
                    [link]="'/discover'"
                    [iconName]="'ui-arrow-forward'"
                    [iconPosition]="LinkIconPosition.RIGHT"
                    [weight]="LinkWeight.MEDIUM"
                    [color]="LinkColor.INHERIT"
                    [iconColor]="IconColor.INHERIT"
                    [iconAnimation]="true"
                  >
                    View all
                  </fl-link>
                </div>
              </fl-col>
              <fl-col [col]="6">
                <app-navigation-menu-card
                  class="mb-mid"
                  i18n-description="
                    How to earn money at freelancer.com article description
                  "
                  description="How to get started earning money freelancing"
                  [url]="
                    '/community/articles/how-to-earn-money-at-freelancer-com'
                  "
                  [imgSrc]="
                    'logged-out-navigation/solutions/how-it-works-2.png'
                  "
                  [readMore]="true"
                  [longDescription]="true"
                  [descriptionWeight]="FontWeight.NORMAL"
                  [isLoggedOut]="true"
                >
                </app-navigation-menu-card>

                <div class="ColHeader mb-small">
                  <fl-icon
                    class="ViewMoreColor mr-xxsmall"
                    [name]="'ui-book'"
                    [useIconFont]="true"
                    [fillIconFont]="false"
                    [color]="IconColor.INHERIT"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                  <p
                    class="text-light font-bold"
                    i18n="Resources header"
                  >
                    Resources
                  </p>
                </div>
                <app-navigation-logged-out-home-section-resources
                  class="Resources"
                ></app-navigation-logged-out-home-section-resources>
                <fl-link
                  flTrackingLabel="ViewAllClick"
                  i18n="View all link"
                  class="ViewMoreColor"
                  [link]="'/articles'"
                  [iconName]="'ui-arrow-forward'"
                  [iconPosition]="LinkIconPosition.RIGHT"
                  [weight]="LinkWeight.MEDIUM"
                  [color]="LinkColor.INHERIT"
                  [iconColor]="IconColor.INHERIT"
                  [iconAnimation]="true"
                >
                  View all
                </fl-link>
              </fl-col>
            </fl-grid>
          </div>
        </div>
      </fl-callout-content>
    </fl-callout>

    <ng-template #innovationChallengesOff>
      <app-navigation-menu-nav-item
        class="CardLink mb-mid"
        i18n-titleLink="Government navigation item title text"
        titleLink="Government"
        i18n-description="Government navigation item description text"
        description="Solve critical national challenges by crowdsourcing innovative solutions."
        [descriptionColor]="FontColor.LIGHT"
        [isLoggedOut]="true"
        [doubleColumn]="true"
        [iconName]="'ui-bank'"
        [iconColor]="IconColor.RECRUITER_LIGHT"
        [iconArrow]="false"
        [isActive]="true"
        [url]="'/enterprise'"
        [fragment]="'Government'"
      ></app-navigation-menu-nav-item>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./navigation-logged-out-home-section-solutions.component.scss'],
})
export class NavigationLoggedOutHomeSectionSolutionsComponent {
  CalloutPlacement = CalloutPlacement;
  CalloutColor = CalloutColor;
  Feature = Feature;
  FontWeight = FontWeight;
  FontColor = FontColor;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkIconPosition = LinkIconPosition;
  LinkWeight = LinkWeight;
  LinkUnderline = LinkUnderline;
  LogoSize = LogoSize;
  TextSize = TextSize;

  isCalloutOpen = false;

  openCalloutHandler(): void {
    this.isCalloutOpen = true;
  }

  closeCalloutHandler(): void {
    this.isCalloutOpen = false;
  }
}
